import { trackProductEvent } from '../lib/product-analytics';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  LibraryCandidateKind,
  LibraryDecision,
  LibraryProjectPipelineStage,
  LibrarySort,
  LibraryStage,
  LibrarySource,
} from '../lib/library/types';
import { useToast } from './useToast';
import {
  attachLibraryProjectCandidates,
  createLibraryCollection,
  createLibraryProject,
  deleteLibraryCollection,
  deleteLibraryProject,
  deleteLibraryRole,
  detachLibraryProjectCandidate,
  fetchLibraryCandidateDetail,
  fetchLibraryCandidates,
  fetchLibraryCollectionDetail,
  fetchLibraryCollections,
  fetchLibraryEmailForwardAddress,
  fetchLibraryOverview,
  fetchLibraryProject,
  fetchLibraryProjects,
  fetchLibraryRoles,
  fetchRoleOverview,
  moveLibraryProjectCandidateStage,
  rejectLibraryCandidate,
  setLibraryCollectionCandidates,
  starLibraryCandidate,
  toggleLibraryRoleStatus,
  unstarLibraryCandidate,
  updateLibraryCollection,
  updateLibraryProject,
  updateLibraryRole,
  uploadLibraryCandidate,
  type CreateLibraryCollectionInput,
  type CreateLibraryProjectInput,
  type FetchLibraryCandidatesParams,
  type UpdateLibraryCollectionInput,
  type UpdateLibraryProjectInput,
  type UpdateLibraryRoleInput,
} from '../lib/library-api';
import { QUERY_KEYS } from '../lib/query-keys';
import { useWorkspace } from '../providers/useWorkspace';

function serializeCandidatesParams(params: FetchLibraryCandidatesParams = {}) {
  return JSON.stringify({
    roleId: params.roleId ?? null,
    q: params.q ?? '',
    sort: params.sort ?? 'newest',
    stages: params.stages ?? [],
    sources: params.sources ?? [],
    decisions: params.decisions ?? [],
    locations: params.locations ?? [],
    collectionIds: params.collectionIds ?? [],
    candidateKinds: params.candidateKinds ?? [],
    starredOnly: params.starredOnly ?? false,
    cursor: params.cursor ?? null,
    limit: params.limit ?? 50,
  });
}

function invalidateLibraryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId?: string | null,
) {
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LIBRARY_OVERVIEW(companyId) });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LIBRARY_ROLES(companyId) });
  queryClient.invalidateQueries({ queryKey: ['library', companyId ?? 'none', 'candidates'] });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LIBRARY_COLLECTIONS(companyId) });
}

export function useLibraryOverview(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_OVERVIEW(companyId),
    queryFn: fetchLibraryOverview,
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useLibraryRoles(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_ROLES(companyId),
    queryFn: fetchLibraryRoles,
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useRoleOverview(roleId: string, enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: ['library', companyId ?? 'none', 'role', roleId, 'overview'] as const,
    queryFn: () => fetchRoleOverview(roleId),
    enabled: Boolean(companyId) && Boolean(roleId) && enabled,
    staleTime: 15_000,
  });
}

export function useLibraryCollections(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_COLLECTIONS(companyId),
    queryFn: fetchLibraryCollections,
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useLibraryEmailForwardAddress(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_EMAIL_ADDRESS(companyId),
    queryFn: fetchLibraryEmailForwardAddress,
    enabled: Boolean(companyId) && enabled,
    staleTime: 60_000,
  });
}

export function useLibraryCandidates(
  params: FetchLibraryCandidatesParams = {},
  enabled = true,
) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_CANDIDATES(
      companyId,
      serializeCandidatesParams(params),
    ),
    queryFn: () => fetchLibraryCandidates(params),
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useLibraryCandidateDetail(
  candidateId: string,
  searchSessionId?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_CANDIDATE_DETAIL(candidateId, searchSessionId),
    queryFn: () => fetchLibraryCandidateDetail(candidateId, searchSessionId),
    enabled: enabled && Boolean(candidateId),
    staleTime: 15_000,
  });
}

export function useLibraryCollectionDetail(collectionId: string, enabled = true) {
  return useQuery({
    queryKey: ['library', 'collection', collectionId],
    queryFn: () => fetchLibraryCollectionDetail(collectionId),
    enabled: enabled && Boolean(collectionId),
    staleTime: 15_000,
  });
}

export function useUpdateLibraryRole() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({ roleId, input }: { roleId: string; input: UpdateLibraryRoleInput }) =>
      updateLibraryRole(roleId, input),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useToggleLibraryRoleStatus() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (roleId: string) => toggleLibraryRoleStatus(roleId),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useDeleteLibraryRole() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (roleId: string) => deleteLibraryRole(roleId),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useRejectLibraryCandidate() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (candidateId: string) => rejectLibraryCandidate(candidateId),
    onSuccess: (candidate) => {
      trackProductEvent('library_candidate_decided', { decision: 'reject' });
      invalidateLibraryQueries(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_CANDIDATE_DETAIL_SCOPE(candidate.id),
      });
    },
  });
}

export function useToggleLibraryCandidateStar() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: async ({
      candidateId,
      starred,
    }: {
      candidateId: string;
      starred: boolean;
    }) => {
      if (starred) {
        await unstarLibraryCandidate(candidateId);
        trackProductEvent('library_candidate_decided', { decision: 'unstar' });
        return { candidateId };
      }

      const result = await starLibraryCandidate(candidateId);
      trackProductEvent('library_candidate_decided', { decision: 'star' });
      return result;
    },
    onSuccess: (result) => {
      invalidateLibraryQueries(queryClient, companyId);
      const candidateId =
        'candidateId' in result ? result.candidateId : result.id;
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_CANDIDATE_DETAIL_SCOPE(candidateId),
      });
    },
  });
}

export function useCreateLibraryCollection() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (input: CreateLibraryCollectionInput) =>
      createLibraryCollection(input),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useUpdateLibraryCollection() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      collectionId,
      input,
    }: {
      collectionId: string;
      input: UpdateLibraryCollectionInput;
    }) => updateLibraryCollection(collectionId, input),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useDeleteLibraryCollection() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (collectionId: string) => deleteLibraryCollection(collectionId),
    onSuccess: () => invalidateLibraryQueries(queryClient, companyId),
  });
}

export function useSetLibraryCollectionCandidates() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({
      collectionId,
      candidateIds,
    }: {
      collectionId: string;
      candidateIds: string[];
    }) => setLibraryCollectionCandidates(collectionId, candidateIds),
    onSuccess: (_collection, variables) => {
      invalidateLibraryQueries(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: ['library', 'collection', variables.collectionId],
      });
    },
  });
}

export function useUploadLibraryCandidate() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (formData: FormData) => uploadLibraryCandidate(formData),
    onSuccess: (candidate) => {
      // The recruiter data-in loop that gives a library project its value.
      trackProductEvent('library_candidate_uploaded', { source: 'library' });
      invalidateLibraryQueries(queryClient, companyId);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_CANDIDATE_DETAIL_SCOPE(candidate.id),
      });
    },
  });
}

export type LibraryFilterQueryParams = {
  roleId?: string;
  q?: string;
  sort?: LibrarySort;
  stages?: LibraryStage[];
  sources?: LibrarySource[];
  decisions?: Exclude<LibraryDecision, 'none'>[];
  locations?: string[];
  collectionIds?: string[];
  candidateKinds?: LibraryCandidateKind[];
  starredOnly?: boolean;
};

function invalidateProjectQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  companyId?: string | null,
  projectId?: string | null,
) {
  queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.LIBRARY_PROJECTS(companyId),
  });
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.LIBRARY_PROJECT_DETAIL(companyId, projectId),
    });
  }
}

function useProjectMutationOnError() {
  const { t } = useTranslation('common');
  const toast = useToast();
  return (error: unknown) => {
    toast.error(
      t('errors.generic'),
      error instanceof Error ? error.message : undefined,
    );
  };
}

export function useLibraryProjects(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_PROJECTS(companyId),
    queryFn: fetchLibraryProjects,
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useLibraryProject(projectId: string, enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_PROJECT_DETAIL(companyId, projectId),
    queryFn: () => fetchLibraryProject(projectId),
    enabled: enabled && Boolean(companyId) && Boolean(projectId),
    staleTime: 15_000,
  });
}

export function useCreateLibraryProject() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: (input: CreateLibraryProjectInput) =>
      createLibraryProject(input),
    onSuccess: () => invalidateProjectQueries(queryClient, companyId),
    onError,
  });
}

export function useUpdateLibraryProject() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: string;
      input: UpdateLibraryProjectInput;
    }) => updateLibraryProject(projectId, input),
    onSuccess: (_data, variables) =>
      invalidateProjectQueries(queryClient, companyId, variables.projectId),
    onError,
  });
}

export function useDeleteLibraryProject() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: (projectId: string) => deleteLibraryProject(projectId),
    onSuccess: (_data, projectId) =>
      invalidateProjectQueries(queryClient, companyId, projectId),
    onError,
  });
}

export function useAttachLibraryProjectCandidates() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: ({
      projectId,
      candidateIds,
      pipelineStage,
    }: {
      projectId: string;
      candidateIds: string[];
      pipelineStage?: LibraryProjectPipelineStage;
    }) => attachLibraryProjectCandidates(projectId, candidateIds, pipelineStage),
    onSuccess: (_data, variables) =>
      invalidateProjectQueries(queryClient, companyId, variables.projectId),
    onError,
  });
}

export function useDetachLibraryProjectCandidate() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: ({
      projectId,
      candidateId,
    }: {
      projectId: string;
      candidateId: string;
    }) => detachLibraryProjectCandidate(projectId, candidateId),
    onSuccess: (_data, variables) =>
      invalidateProjectQueries(queryClient, companyId, variables.projectId),
    onError,
  });
}

export function useMoveLibraryProjectCandidateStage() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();
  const onError = useProjectMutationOnError();

  return useMutation({
    mutationFn: ({
      projectId,
      candidateId,
      pipelineStage,
    }: {
      projectId: string;
      candidateId: string;
      pipelineStage: LibraryProjectPipelineStage;
    }) =>
      moveLibraryProjectCandidateStage(projectId, candidateId, pipelineStage),
    onSuccess: (_data, variables) => {
      // The ATS funnel itself: stage moves are what time-to-hire is made of.
      trackProductEvent('library_pipeline_stage_moved', {
        to_stage: variables.pipelineStage,
      });
      invalidateProjectQueries(queryClient, companyId, variables.projectId);
    },
    onError,
  });
}
